/**
 * RulePreviewActionCard - Renderização estilo WhatsApp para preview de ação
 *
 * Cada ação é renderizada como:
 *  - Bolha de saída (outgoing bubble) verde para mensagens enviadas ao contato
 *    (texto, mídia, link, botão com link, template)
 *  - "System message" central (pílula) para ações operacionais
 *    (delay, atribuir agente/departamento/fase/fila, adicionar/remover tag, prioridade)
 *
 * @version 2.1.1
 * @date 2026-07-09
 *
 * CHANGELOG:
 *   v2.1.1 - Fix: MediaBubble extraído para arquivo próprio (RulePreviewMediaBubble.tsx)
 *            para respeitar o boundary do React Fast Refresh. Corrige
 *            "ReferenceError: MediaBubble is not defined" em runtime após HMR.
 *            OutgoingBubble e ReadTicks passam a ser exportados para reuso.
 *   v2.1.0 - Mídia (áudio/vídeo/imagem/documento) agora é previewável no card.
 *   v2.0.0 - Redesign completo no formato chat WhatsApp (bolhas + ticks + system pills).
 *   v1.0.0 - Versão inicial em cards neutros.
 */

import {
  MessageSquare,
  Image as ImageIcon,
  Video,
  Music,
  FileText,
  Timer,
  Link as LinkIcon,
  Building2,
  User,
  Tag,
  MinusCircle,
  Star,
  LayoutDashboard,
  Users,
  MousePointerClick,
  FileSignature,
  CheckCheck,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { MediaBubble } from './RulePreviewMediaBubble';

// =====================================================
// INTERFACES
// =====================================================

interface RulePreviewActionCardProps {
  actionType: string;
  actionValue: Record<string, unknown>;
  delaySeconds?: number;
  index: number;
  departmentName?: string;
  agentName?: string;
  tagName?: string;
  phaseName?: string;
  queueName?: string;
}

// Mapeamento de ícones por tipo
const typeIcons: Record<string, React.ElementType> = {
  send_text: MessageSquare,
  send_image: ImageIcon,
  send_video: Video,
  send_audio: Music,
  send_document: FileText,
  send_link: LinkIcon,
  assign_department: Building2,
  assign_agent: User,
  add_tag: Tag,
  remove_tag: MinusCircle,
  set_priority: Star,
  assign_phase: LayoutDashboard,
  assign_global_queue: Users,
  send_button_link: MousePointerClick,
  send_template: FileSignature,
  delay: Timer,
};

// Ações renderizadas como mensagem de saída (bolha verde)
const OUTGOING_ACTIONS = new Set([
  'send_text',
  'send_image',
  'send_video',
  'send_audio',
  'send_document',
  'send_link',
  'send_button_link',
  'send_template',
]);

// =====================================================
// HELPERS
// =====================================================

/** Extrai apenas o nome do arquivo de um caminho do Storage, removendo timestamp prefix */
function extractFileName(path: string | undefined): string {
  if (!path) return 'Arquivo';
  const segments = path.split('/');
  const fullName = segments[segments.length - 1];
  const timestampPattern = /^\d{13}_/;
  return timestampPattern.test(fullName) ? fullName.replace(timestampPattern, '') : fullName;
}

// isPreviewableUrl foi movido para RulePreviewMediaBubble.tsx (uso exclusivo lá).



/** Gera horário simulado incrementando por índice — puramente estético */
function fakeTime(index: number): string {
  const base = 10 * 60 + 42; // 10:42
  const total = base + index;
  const h = Math.floor(total / 60) % 24;
  const m = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Ticks duplos azuis (mensagem "lida") - estilo WhatsApp. Exportado para reuso em MediaBubble. */
export function ReadTicks() {
  return <CheckCheck className="h-3.5 w-3.5 text-sky-500" aria-hidden />;
}

// =====================================================
// SUB-COMPONENTES
// =====================================================

/** Bolha de saída estilo WhatsApp. Exportado para reuso em MediaBubble. */
export function OutgoingBubble({
  children,
  time,
  noPadding = false,
  footer,
  showInlineTime = true,
}: {
  children: React.ReactNode;
  time: string;
  noPadding?: boolean;
  footer?: React.ReactNode;
  showInlineTime?: boolean;
}) {
  return (
    <div className="flex justify-end">
      <div
        className={cn(
          'relative max-w-[85%] rounded-lg rounded-tr-none shadow-sm overflow-hidden',
          'bg-[#dcf8c6] dark:bg-emerald-900/40',
        )}
      >
        <div className={cn(noPadding ? '' : 'px-3 py-1.5')}>
          {children}
          {showInlineTime && (
            <div className="mt-1 flex items-center justify-end gap-1">
              <span className="text-[10px] text-slate-500 dark:text-slate-300/70 uppercase">
                {time}
              </span>
              <ReadTicks />
            </div>
          )}
        </div>
        {footer}
      </div>
    </div>
  );
}

/** Pílula central tipo "system message" para ações operacionais */
function SystemPill({
  icon: Icon,
  children,
  tone = 'default',
}: {
  icon: React.ElementType;
  children: React.ReactNode;
  tone?: 'default' | 'danger' | 'info';
}) {
  return (
    <div className="flex justify-center my-2">
      <div
        className={cn(
          'bg-white/85 dark:bg-slate-800/85 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm',
          'flex items-center gap-1.5',
        )}
      >
        <Icon
          className={cn(
            'h-3 w-3',
            tone === 'danger' && 'text-red-600',
            tone === 'info' && 'text-sky-600',
            tone === 'default' && 'text-slate-600 dark:text-slate-300',
          )}
        />
        <span className="text-[11px] text-slate-700 dark:text-slate-200 font-medium uppercase tracking-wider">
          {children}
        </span>
      </div>
    </div>
  );
}

// MediaBubble foi movido para ./RulePreviewMediaBubble.tsx (v2.1.1) para
// respeitar o boundary do React Fast Refresh.





// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function RulePreviewActionCard({
  actionType,
  actionValue,
  delaySeconds,
  index,
  departmentName,
  agentName,
  tagName,
  phaseName,
  queueName,
}: RulePreviewActionCardProps) {
  const Icon = typeIcons[actionType] || MessageSquare;
  const time = fakeTime(index);
  const isOutgoing = OUTGOING_ACTIONS.has(actionType);

  // ---------- AÇÕES OPERACIONAIS (system pills) ----------
  if (!isOutgoing) {
    if (actionType === 'delay') {
      return (
        <SystemPill icon={Timer} tone="info">
          Aguardar {delaySeconds || 0} segundos
        </SystemPill>
      );
    }

    if (actionType === 'assign_department') {
      return <SystemPill icon={Building2}>Atribuir departamento: {departmentName || '—'}</SystemPill>;
    }

    if (actionType === 'assign_agent') {
      return <SystemPill icon={User}>Atribuir agente: {agentName || '—'}</SystemPill>;
    }

    if (actionType === 'add_tag') {
      return <SystemPill icon={Tag}>Adicionar tag: {tagName || '—'}</SystemPill>;
    }

    if (actionType === 'remove_tag') {
      const mode = actionValue.remove_tag_mode as string;
      return (
        <SystemPill icon={MinusCircle} tone="danger">
          {mode === 'all' ? 'Remover todas as tags' : `Remover tag: ${tagName || '—'}`}
        </SystemPill>
      );
    }

    if (actionType === 'set_priority') {
      return (
        <SystemPill icon={Star}>
          Prioridade: {(actionValue.priority as number) ?? 5}
        </SystemPill>
      );
    }

    if (actionType === 'assign_phase') {
      return <SystemPill icon={LayoutDashboard}>Mover para fase: {phaseName || '—'}</SystemPill>;
    }

    if (actionType === 'assign_global_queue') {
      return <SystemPill icon={Users}>Distribuir via fila: {queueName || '—'}</SystemPill>;
    }

    return <SystemPill icon={Icon}>Ação desconhecida</SystemPill>;
  }

  // ---------- MENSAGENS DE SAÍDA (bolhas verdes) ----------
  switch (actionType) {
    case 'send_text': {
      const message = (actionValue.message as string) || 'Mensagem não definida';
      return (
        <OutgoingBubble time={time}>
          <p className="text-[14.5px] text-[#111b21] dark:text-slate-100 leading-snug whitespace-pre-wrap pr-12">
            {message}
          </p>
        </OutgoingBubble>
      );
    }

    case 'send_image':
    case 'send_video':
    case 'send_audio':
    case 'send_document': {
      const mediaUrl = actionValue.media_url as string;
      const caption = actionValue.caption as string;
      const filename = (actionValue.filename as string) || extractFileName(mediaUrl);
      return (
        <MediaBubble
          actionType={actionType}
          mediaUrl={mediaUrl}
          caption={caption}
          filename={filename}
          time={time}
          Icon={Icon}
        />
      );
    }


    case 'send_link': {
      const url = (actionValue.link_url || actionValue.url) as string;
      const title = (actionValue.title as string) || 'Link';
      return (
        <OutgoingBubble time={time}>
          <div className="pr-12 space-y-0.5">
            <p className="text-[13px] font-semibold text-[#111b21] dark:text-slate-100 truncate">
              {title}
            </p>
            <p className="text-[12px] text-sky-700 dark:text-sky-300 truncate flex items-center gap-1">
              <ExternalLink className="h-3 w-3 shrink-0" />
              {url}
            </p>
          </div>
        </OutgoingBubble>
      );
    }

    case 'send_button_link': {
      const message = (actionValue.message as string) || 'Mensagem';
      const btnLabel = (actionValue.button_label as string) || 'Abrir';
      const btnUrl = actionValue.link_url as string;
      return (
        <OutgoingBubble
          time={time}
          noPadding
          showInlineTime={false}
          footer={
            <div className="border-t border-emerald-200/60 dark:border-emerald-700/40 bg-white/40 dark:bg-slate-900/30">
              <div
                className="w-full py-2.5 px-4 text-sky-600 dark:text-sky-300 font-medium text-[14px] flex items-center justify-center gap-2"
                title={btnUrl}
              >
                <ExternalLink className="h-4 w-4" />
                {btnLabel}
              </div>
            </div>
          }
        >
          <div className="px-3 pt-2 pb-1">
            <p className="text-[14.5px] text-[#111b21] dark:text-slate-100 leading-snug whitespace-pre-wrap">
              {message}
            </p>
            <div className="mt-1 flex items-center justify-end gap-1">
              <span className="text-[10px] text-slate-500 dark:text-slate-300/70 uppercase">
                {time}
              </span>
              <ReadTicks />
            </div>
          </div>
        </OutgoingBubble>
      );
    }

    case 'send_template': {
      const tplName = (actionValue.template_name as string) || 'Template';
      const tplLang = (actionValue.template_language as string) || 'pt_BR';
      return (
        <OutgoingBubble time={time}>
          <div className="pr-12 space-y-0.5">
            <div className="flex items-center gap-1.5">
              <FileSignature className="h-3.5 w-3.5 text-emerald-700 dark:text-emerald-300" />
              <p className="text-[13px] font-semibold text-[#111b21] dark:text-slate-100">
                {tplName}
              </p>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-300/70 uppercase tracking-wide">
              Template WhatsApp · {tplLang}
            </p>
          </div>
        </OutgoingBubble>
      );
    }

    default:
      return null;
  }
}
